import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal } from '../comps';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import MaterialInput from 'src/components/form/MaterialInput';
import { InputLabel, Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import { Guard } from '../Guard';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { useCache } from 'src/graphql';
import { Role, useInviteUsersMutation } from 'src/graphql/_generated';
import { RoleSelect } from '../../select';
import { AvailableRoles, ExtendedInvitedUserRoles } from './comps';

const StyledModal = styled(Modal)`
  width: 635px;
  min-height: 200px;
  padding: 0;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .btns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

interface AddUserModalProps {
  onClose: () => void;
  emailsInUse: string[];
  initialValues?: { ownerId?: string; name: string; email: string };
  type: 'workspace' | 'organisation';

  id: string;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({ onClose, emailsInUse, initialValues = {}, type, id }) => {
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [invitedRoles, setInvitedRoles] = useState<ExtendedInvitedUserRoles | undefined>(undefined);
  const { refetchQuery } = useCache();

  const validationSchema = Yup.object({
    name: Yup.string().required('Required').max(100, 'Max 100 characters'),
    email: Yup.string()
      .required('Required')
      .email('Invalid email')
      .max(100, 'Max 100 characters')
      .notOneOf([...emailsInUse], 'This email is already in use'),
    role: Yup.string().required('Required'),
  });

  const [invite, { error }] = useInviteUsersMutation({
    onCompleted: () => {
      refetchQuery({ include: ['profile', type] });
      onClose();
    },
  });

  const {
    values,
    handleBlur,
    handleChange,
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    isValid,
    dirty,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: '',
      email: '',
      ownerId: undefined,
      role: null as Role | null,
      ...initialValues,
    },
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      return invite({
        variables: {
          workspaceId: type === 'workspace' ? id : undefined,
          organisationId: type === 'organisation' ? id : undefined,
          invitesData: [
            {
              ownerId: values.ownerId,
              name: values.name,
              email: values.email,
              role: values.role,
            },
          ],
        },
      });
    },
  });

  useEffect(() => {
    setFieldValue('role', null);
    setInvitedRoles(undefined);
  }, [values.email]);

  const isWorkspace = type === 'workspace';

  const getDisabledOptions = (): { value: Role; reason: string }[] => {
    const disabledOptions: { value: Role; reason: string }[] = [];

    const partnerAdminRestrictionReason =
      'User is a Partner Branch Admin and cannot be invited to workspaces. Invite a new user profile using an alias: name_surname+nonadmin@domain.com to allow this user access to this workspace.';

    const isPartnerAdminReason =
      'User is a Partner Branch Admin and cannot be assigned roles within organisations. Invite a new user profile using an alias: name_surname+nonadmin@domain.com to allow this user access to this organisation';

    const permissionReason = invitedRoles?.isTrustedEmail
      ? 'Insufficient permissions'
      : 'The platform does not allow the user to be invited as an admin until they have signed up and logged into the platform.';

    if (isWorkspace) {
      // In workspace context, org-level roles are always disabled with context reason
      disabledOptions.push({
        value: Role.OrganisationAdmin,
        reason: invitedRoles?.isPartnerAdminWorkspaceRestriction
          ? partnerAdminRestrictionReason
          : 'This is an organisation-level role. Switch to parent organisation to assign this role to the user.',
      });
      disabledOptions.push({
        value: Role.PartnerBranchAdmin,
        reason: invitedRoles?.isPartnerAdminWorkspaceRestriction
          ? partnerAdminRestrictionReason
          : 'This is an organisation-level role. Switch to parent organisation to assign this role to the user.',
      });

      // For workspace-appropriate roles, check if they're allowed by permissions
      const workspaceRoles = [Role.WorkspaceAdmin, Role.CustomerManager, Role.Collaborator];
      workspaceRoles.forEach((role) => {
        if (invitedRoles?.isPartnerAdminWorkspaceRestriction || !invitedRoles?.roles?.includes(role)) {
          disabledOptions.push({
            value: role,
            reason: invitedRoles?.isPartnerAdminWorkspaceRestriction ? partnerAdminRestrictionReason : permissionReason,
          });
        }
      });
    } else {
      // In organisation context, workspace-level role is always disabled with context reason
      disabledOptions.push({
        value: Role.WorkspaceAdmin,
        reason: invitedRoles?.isPartnerAdmin
          ? isPartnerAdminReason
          : 'This is a workspace-level role. Switch to a workspace to assign this role to the user.',
      });

      // For org-appropriate roles, check if they're allowed by permissions
      const orgRoles = [Role.OrganisationAdmin, Role.PartnerBranchAdmin, Role.CustomerManager, Role.Collaborator];
      orgRoles.forEach((role) => {
        if (!invitedRoles?.roles?.includes(role)) {
          let reason = invitedRoles?.isPartnerAdmin ? isPartnerAdminReason : permissionReason;
          if (!invitedRoles?.isPartnerAdmin && role === Role.PartnerBranchAdmin && invitedRoles?.isTrustedEmail) {
            reason = `To assign this user as a Partner Branch Admin, you must first clear their existing permissions. Alternatively, you can invite a new user profile using an alias: name_surname+admin@domain.com.`;
          }
          disabledOptions.push({ value: role, reason });
        }
      });
    }

    return disabledOptions;
  };

  const mxTag = isWorkspace ? 'WORKSPACE' : 'ORGANISATION';
  const mxModal = isWorkspace ? 'Workspace add user modal' : 'Organisation add user modal';

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen>
          <Spacer $px={30} />
          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              Add a User
            </Poppins>
            <Spacer $px={20} />
            <InputLabel>NAME *</InputLabel>
            <MaterialInput
              name="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder="Name"
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              fullWidth
            />
            <Spacer $px={20} />
            <InputLabel>EMAIL *</InputLabel>
            <MaterialInput
              name="email"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              type="email"
              placeholder="Email"
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              fullWidth
            />
            <Spacer $px={20} />
            {invitedRoles ? (
              <>
                <InputLabel>ROLE *</InputLabel>
                <RoleSelect
                  onChange={(value) => setFieldValue('role', value.value)}
                  value={values.role}
                  filteredOutRoles={[Role.NoAccess]}
                  disabledOptions={getDisabledOptions()}
                />
              </>
            ) : (
              <>
                <Spacer $px={10} />
                <AvailableRoles
                  onAvailableRoles={setInvitedRoles}
                  email={values.email}
                  workspaceId={isWorkspace ? id : undefined}
                  organisationId={!isWorkspace ? id : undefined}
                />
              </>
            )}
            <Spacer $px={40} />
            <div className="btns">
              <Button
                secondary
                onClick={() => {
                  setIsCloseRequested(true);
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Cancel',
                    tags: [mxTag],
                    modal: mxModal,
                  });
                }}
                primary
              >
                CANCEL
              </Button>
              <Button
                primary
                disabled={!isValid || isSubmitting || !dirty}
                onClick={() => {
                  handleSubmit();
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Invite',
                    tags: [mxTag],
                    modal: mxModal,
                  });
                }}
              >
                INVITE
              </Button>
            </div>

            {!!error && (
              <>
                <Spacer $px={15} />
                <div className="error">{error.message || 'Something went wrong'}</div>
              </>
            )}
          </div>

          <Spacer $px={30} />
        </StyledModal>
      }
    />
  );
};
